import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtPrice, fmtUsd } from "@/lib/format";

/**
 * Fills / Open-Orders panel — v2 (Feb 2026, msg 501+).
 *
 * Rules enforced by this component:
 *  • ONE block per active exchange (dynamic — no hardcoded exchange name).
 *  • Max 5 open orders visible; overflow scrolls internally.
 *  • Open orders and Recent fills are two clearly-separated sub-sections.
 *  • LIVE badge appears only when the exchange is authenticated AND
 *    trading is really authorised (state.active_exchanges[i].mode==='LIVE').
 *  • Simulator, disabled exchanges → never rendered in the active view.
 *  • When no exchange is active → "no active exchange" message.
 */
export default function FillsTable({ state }) {
  const { t } = useI18n();
  const active = useMemo(
    () => (state?.active_exchanges || []).filter((x) => x.enabled),
    [state?.active_exchanges],
  );

  if (state?.no_active_exchange || active.length === 0) {
    return (
      <div className="mq-panel" data-testid="fills-table">
        <div className="mq-panel-header">
          <span className="mq-heading text-sm font-semibold tracking-wider">
            {t("fills")}
          </span>
        </div>
        <div
          data-testid="fills-no-active"
          className="px-4 py-6 text-xs text-[var(--mq-text-3)] italic"
        >
          {t("noActiveExchange")}
        </div>
      </div>
    );
  }

  return (
    <div className="mq-panel" data-testid="fills-table">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">
          {t("fills")}
        </span>
        <span className="mq-label" data-testid="fills-active-count">
          {active.length} {active.length > 1 ? t("exchangesLower") : t("exchange")}
        </span>
      </div>
      <div className="flex flex-col divide-y" style={{ borderColor: "var(--mq-border)" }}>
        {active.map((ex) => (
          <ExchangeBlock key={ex.name} exchange={ex} />
        ))}
      </div>
    </div>
  );
}

function ExchangeBlock({ exchange }) {
  const { t } = useI18n();
  const [openOrders, setOpenOrders] = useState([]);
  const [fills, setFills] = useState([]);
  const [error, setError] = useState("");
  const name = exchange.name;
  const isSimulator = name === "simulator";
  const showLive = exchange.mode === "LIVE";

  const load = async () => {
    try {
      if (isSimulator) {
        // Simulator = local bot fills (kept for audit but restricted view).
        const { data } = await api.get("/fills", {
          params: { limit: 25, exchange: "simulator" },
        });
        setFills((data || []).slice(0, 25));
        setOpenOrders([]);
        setError("");
        return;
      }
      // Real exchange — pull authoritative open orders + trades.
      const [oo, tr] = await Promise.all([
        api.get(`/exchange/${name}/open_orders`),
        api.get(`/exchange/${name}/trades`, { params: { limit: 25 } }),
      ]);
      setOpenOrders(oo.data?.open_orders || []);
      const trades = (tr.data?.trades || []).map((x) => ({
        ts: x.ts,
        side: x.side,
        price: x.price,
        qty: x.qty,
        usd: x.cost_usd,
      }));
      setFills(trades);
      setError(oo.data?.error || tr.data?.error || "");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || String(err));
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, exchange.api_connected, exchange.mode]);

  // Status pill styling — clear separation between "worker running" and
  // "this exchange is really LIVE".
  const modeColor = showLive
    ? "var(--mq-green)"
    : exchange.mode === "DRY-RUN"
      ? "var(--mq-yellow)"
      : "var(--mq-text-3)";

  return (
    <div className="p-3" data-testid={`ex-block-${name}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span className="uppercase font-semibold text-xs tracking-wider">
            {name}
          </span>
          <span className="text-[10px] uppercase mq-label">{exchange.symbol}</span>
          {showLive && (
            <span
              data-testid={`ex-live-${name}`}
              className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-sm"
              style={{ background: "rgba(0,255,127,0.12)", color: "var(--mq-green)" }}
            >
              · LIVE
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid={`ex-status-${name}`}
            className="text-[10px] uppercase font-mono"
            style={{ color: modeColor }}
          >
            {exchange.mode} · {exchange.api_status}
          </span>
        </div>
      </div>

      {exchange.last_api_error && !showLive && (
        <div
          data-testid={`ex-api-error-${name}`}
          className="mb-2 px-2 py-1 text-[10px] font-mono"
          style={{ color: "var(--mq-red)", background: "rgba(255,64,64,0.06)" }}
        >
          {exchange.last_api_error}
        </div>
      )}

      {error && (
        <div
          data-testid={`ex-fetch-error-${name}`}
          className="mb-2 px-2 py-1 text-[10px] font-mono"
          style={{ color: "var(--mq-red)" }}
        >
          {error}
        </div>
      )}

      {/* Open Orders — max 5 rows visible then internal scroll */}
      <div className="mb-2">
        <div className="mq-label mb-1" style={{ color: "var(--mq-cyan)" }}>
          {t("openOrders")} · {openOrders.length}
        </div>
        {openOrders.length === 0 ? (
          <div className="text-[11px] text-[var(--mq-text-3)] italic px-1 py-1">
            {t("noOpenOrders")}
          </div>
        ) : (
          <div
            data-testid={`open-orders-scroll-${name}`}
            style={{ maxHeight: 5 * 26, overflowY: "auto" }}
          >
            <table className="w-full text-xs font-mono">
              <tbody>
                {openOrders.map((o) => (
                  <tr
                    key={o.id}
                    data-testid={`open-order-row-${name}`}
                    className="border-t"
                    style={{ borderColor: "var(--mq-border)" }}
                  >
                    <td className="px-2 py-1 text-[var(--mq-text-2)]">
                      {o.ts ? new Date(o.ts * 1000).toLocaleTimeString() : "—"}
                    </td>
                    <td
                      className="px-2 py-1 uppercase font-semibold"
                      style={{ color: o.side === "buy" ? "var(--mq-green)" : "var(--mq-red)" }}
                    >
                      {o.side}
                    </td>
                    <td className="px-2 py-1 text-right">{fmtPrice(o.price)}</td>
                    <td className="px-2 py-1 text-right">
                      {Number(o.qty).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                    </td>
                    <td className="px-2 py-1 text-right">{fmtUsd(o.price * o.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent fills (executions) */}
      <div>
        <div className="mq-label mb-1" style={{ color: "var(--mq-magenta)" }}>
          {t("recentFills")} · {fills.length}
        </div>
        {fills.length === 0 ? (
          <div className="text-[11px] text-[var(--mq-text-3)] italic px-1 py-1">
            {t("noData")}
          </div>
        ) : (
          <div
            data-testid={`fills-scroll-${name}`}
            style={{ maxHeight: 5 * 26, overflowY: "auto" }}
          >
            <table className="w-full text-xs font-mono">
              <tbody>
                {fills.slice(0, 25).map((f, i) => (
                  <tr
                    key={`${f.ts}-${i}`}
                    data-testid={`fill-row-${name}`}
                    className="border-t"
                    style={{ borderColor: "var(--mq-border)" }}
                  >
                    <td className="px-2 py-1 text-[var(--mq-text-2)]">
                      {new Date((f.ts || 0) * 1000).toLocaleTimeString()}
                    </td>
                    <td
                      className="px-2 py-1 uppercase font-semibold"
                      style={{ color: f.side === "buy" ? "var(--mq-green)" : "var(--mq-red)" }}
                    >
                      {f.side}
                    </td>
                    <td className="px-2 py-1 text-right">{fmtPrice(f.price)}</td>
                    <td className="px-2 py-1 text-right">
                      {Number(f.qty).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                    </td>
                    <td className="px-2 py-1 text-right">{fmtUsd(f.usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
